import { useRouter } from 'next/router';
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';
import { UrlObject } from 'url';

import { Filter } from 'types/Filter';
import { ProductTypeResult } from 'graphql/queries/GetProductTypes.query';
import { SortId } from 'consts/sort';
import { secondaryQueryFilters } from 'consts/filters';
import { scrollToTop } from 'utils/scroll';
import pruneObject from 'utils/pruneObject';
import keysOf from 'utils/keysOf';
import omit from 'utils/omit';
import pick from 'utils/pick';
import getSortedQueryParams from 'utils/getSortedQueryParams';
import { useIntl } from 'utils/intl';
import clearQuery from 'utils/clearQuery';

import {
  FilterTag,
  getCheckedFiltersCount,
  getCommonQueryParams,
  getFilterTags,
  getMappedFilters,
  MappedFilter,
} from './SearchController.utils';

export type Props = {
  categoryQuery?: ParsedUrlQuery;
  slug?: string;
  filters?: Filter[];
  filtersProductTypeId?: Nullable<number>;
  mainProductTypeId?: Nullable<number>;
  productTypes?: ProductTypeResult[];
};

export type UseSearchStateReturn = {
  page: number;
  limit: number;
  currentSort: SortId;
  isAvailable: boolean;
  combinedQuery: ParsedUrlQuery;
  filtersQuery: ParsedUrlQuery;
  mappedFilters: MappedFilter[];
  mappedAdvancedFilters: MappedFilter[];
  filterTags: FilterTag[];
  numberOfCheckedFilters: number;
  getHref: (newQuery: ParsedUrlQueryInput, excludeCategoryQuery?: boolean) => UrlObject;
  onChangeQuery: (newQuery: ParsedUrlQueryInput) => void;
  getFilterChangeHref: (value: string[], key: string) => UrlObject;
  getFilterClearHref: (value: string[], key: string) => UrlObject;
  onFilterChange: (value: string[], key: string) => void;
  onSubmit: (values: Record<string, string[]>, clearRest?: boolean) => void;
};

const useSearchState = ({
  categoryQuery,
  filters,
  filtersProductTypeId,
  mainProductTypeId,
  productTypes,
  slug,
}: Props): UseSearchStateReturn => {
  const router = useRouter();
  const intl = useIntl();

  const { query } = router;
  const { page, limit, currentSort, isAvailable } = getCommonQueryParams(query);

  const filtersKeys = [...(filters?.map((filter) => filter.key) || []), ...secondaryQueryFilters];
  const categoryQueryKeys = keysOf(categoryQuery || {}) as string[];
  const getQuery = (productTypeId?: Nullable<number>) =>
    ({
      product_type: productTypes?.find(({ id }) => id === productTypeId)?.name,
      ...categoryQuery,
      ...pick(clearQuery(query as Record<string, string>), filtersKeys),
    }) as Record<string, string | string[]>;

  const filtersQuery = getQuery(filtersProductTypeId);
  const combinedQuery = getQuery(mainProductTypeId);
  const mappedFilters = getMappedFilters(filters, categoryQueryKeys, filtersProductTypeId, false);
  const mappedAdvancedFilters = getMappedFilters(filters, categoryQueryKeys, filtersProductTypeId, true);
  const filterTags = getFilterTags(intl, omit(filtersQuery, categoryQueryKeys), filters);
  const numberOfCheckedFilters = getCheckedFiltersCount(omit(filtersQuery, categoryQueryKeys));

  const getHref = (newQuery: ParsedUrlQueryInput): UrlObject => ({
    query: getSortedQueryParams(pruneObject({ ...omit(newQuery, categoryQueryKeys), slug })),
  });

  const onChangeQuery = (newQuery: ParsedUrlQueryInput) => {
    router.push(getHref(newQuery));
    scrollToTop();
  };

  const getFilterChangeHref = (value: string[], key: string): UrlObject =>
    getHref({
      ...combinedQuery,
      [key]: value,
      page: undefined,
    });

  const getFilterClearHref = (value: string[], key: string): UrlObject => {
    const queryValues = combinedQuery[key];

    return getHref({
      ...combinedQuery,
      [key]: Array.isArray(queryValues) ? queryValues?.filter((queryValue) => !value.includes(queryValue)) : undefined,
      page: undefined,
    });
  };

  const onFilterChange = (value: string[], key: string) => {
    onChangeQuery(
      pruneObject({
        ...combinedQuery,
        [key]: value,
        page: undefined,
      })
    );
  };

  const onSubmit = (values: Record<string, string[]>, clearRest?: boolean) => {
    const finalQuery = {
      ...(clearRest ? {} : combinedQuery),
      ...values,
      page: undefined,
    };
    onChangeQuery(
      pruneObject(
        finalQuery,
        (key) =>
          typeof finalQuery[key] === 'undefined' ||
          finalQuery[key] === '' ||
          !(!Array.isArray(finalQuery[key]) || ((finalQuery[key] || []) as unknown[]).filter(Boolean).length)
      )
    );
  };

  return {
    page,
    limit,
    currentSort,
    isAvailable,
    combinedQuery,
    filtersQuery,
    mappedFilters,
    mappedAdvancedFilters,
    filterTags,
    numberOfCheckedFilters,
    getHref,
    onChangeQuery,
    getFilterChangeHref,
    getFilterClearHref,
    onFilterChange,
    onSubmit,
  };
};

export default useSearchState;
